// @clerk/nextjs compat shim — P2 REAL port (not the throwaway probe's
// signed-out stub). Re-exports @clerk/clerk-react's browser-safe surface
// under the `@clerk/nextjs` specifier so every reused apps/web client file
// (51 files import it — FRONTEND_SPA_DESIGN.md §4b) keeps working unmodified.
//
// Version note: apps/web's installed @clerk/nextjs (6.39.5) itself depends
// on @clerk/clerk-react ^5.61.8 under the hood (verified in
// apps/web/node_modules) — so this is the exact package already backing
// today's client-side Clerk behavior, not a new integration. `@clerk/react`
// is Clerk's newer replacement per the deprecation notice on install; stay
// on clerk-react for parity until apps/web itself migrates off @clerk/nextjs.
//
// `@clerk/nextjs/server` (`auth()`, `currentUser()`) is NOT re-exported here
// — those are server-only and already move to Hono middleware in P1; no
// reused client component imports them (verified: client-layout, sidebar,
// auth-entry-page, lab-chat-view, onboarding-modal only touch the hooks/
// components below).
//
// ---------------------------------------------------------------------------
// DEV-BYPASS BRIDGE (dev-flip P2-2) — useUser / useAuth / SignedIn / SignedOut
// ---------------------------------------------------------------------------
// There were TWO disagreeing sources of auth truth in local dev:
//
//   - the route guards ask GET /api/me, which is dev-bypass aware (the server
//     resolves LOCAL_DEV_BYPASS_USER_ID when there is no Clerk session), so
//     they let you into /agents, /library, /admin; while
//   - the reused apps/web components ask Clerk's BROWSER session, which knows
//     nothing about a server-side env var, so they render their signed-out
//     branch behind those same guards ("Sign in to see your visualizations" on
//     a page the guard just admitted you to).
//
// The bypass is the server's identity, so the client's view of it has to come
// from the server too: when Clerk has finished loading and reports NO session,
// but /api/me says we ARE authenticated, these four wrappers answer with the
// identity /api/me returned. Guards and components then agree, and signed-in UI
// becomes testable locally.
//
// WHY THIS IS INERT IN PRODUCTION — two independent reasons, either sufficient:
//
//  1. `import.meta.env.DEV` is a compile-time constant. In `vite build` it is
//     literally `false`, so `DEV && …` folds away and the /api/me call and the
//     synthesized identity are dead code that never runs (and largely does not
//     survive minification).
//  2. Even if it were somehow live: the bypass path is only reachable when Clerk
//     reports a LOADED, SIGNED-OUT session. Whenever a real Clerk session
//     exists, every wrapper returns Clerk's own object untouched — Clerk always
//     wins. And /api/me itself only answers 200-without-a-Clerk-token in
//     development / LOCAL_TEST_SERVE (apps/server/src/auth/effective-user.ts);
//     in production it 401s a session-less request, so the fallback would
//     resolve to `null` and change nothing.
//
// Real Clerk sessions therefore behave identically to before this change, in
// dev and in prod.
import { useEffect, useState, type ReactNode } from 'react';
import {
  useAuth as useClerkAuth,
  useUser as useClerkUser,
} from '@clerk/clerk-react';
import { meOnce, type Me } from '../lib/auth';

export {
  ClerkProvider,
  useClerk,
  useSession,
  useSignIn,
  useSignUp,
  useOrganization,
  useOrganizationList,
  SignInButton,
  SignUpButton,
  UserButton,
  RedirectToSignIn,
  RedirectToSignUp,
} from '@clerk/clerk-react';

/** Compile-time in a production build → the whole bypass path folds away. */
const DEV = import.meta.env.DEV;

type ClerkUserHook = ReturnType<typeof useClerkUser>;
type ClerkAuthHook = ReturnType<typeof useClerkAuth>;

// Resolved once per page load (lib/auth's meOnce memoizes the request), then
// held here so a late-mounting component gets it synchronously.
let cachedMe: Me | null | undefined;

/** `undefined` = still asking; `null` = server says signed out. */
function useDevMe(): Me | null | undefined {
  const [me, setMe] = useState<Me | null | undefined>(DEV ? cachedMe : null);

  useEffect(() => {
    if (!DEV || cachedMe !== undefined) return;
    let alive = true;
    void meOnce().then((resolved) => {
      cachedMe = resolved;
      if (alive) setMe(resolved);
    });
    return () => {
      alive = false;
    };
  }, []);

  return DEV ? me : null;
}

/**
 * The Clerk-shaped user the components read: `user.id`,
 * `user.publicMetadata.role` (→ admin chrome) and `.plan` (→ pro gating) —
 * client-layout.tsx:72,76, lab-chat-view.tsx:2591-2592 — plus the profile
 * fields Clerk exposes. Built from /api/me's row, i.e. the SAME users.role /
 * users.subscription_status the server enforces with, so the UI cannot claim a
 * capability the API would deny.
 */
function devUser(me: Me): ClerkUserHook['user'] {
  return {
    id: me.userId,
    publicMetadata: { role: me.isAdmin ? 'admin' : 'user', plan: me.plan },
    primaryEmailAddress: me.email ? { emailAddress: me.email } : null,
    emailAddresses: me.email ? [{ emailAddress: me.email }] : [],
    fullName: me.name,
    firstName: me.name?.split(' ')[0] ?? null,
    lastName: me.name?.split(' ').slice(1).join(' ') || null,
    imageUrl: me.imageUrl ?? '',
    // Everything else on Clerk's UserResource is method surface no reused
    // component touches (verified by the @clerk/nextjs import inventory above).
  } as unknown as NonNullable<ClerkUserHook['user']>;
}

export function useUser(): ClerkUserHook {
  const clerk = useClerkUser();
  const devMe = useDevMe();

  if (!DEV || clerk.isSignedIn || !clerk.isLoaded) return clerk;
  // Clerk is loaded and signed out — is the SERVER authenticating us anyway?
  if (devMe === undefined) {
    // Still asking. Report "not loaded" rather than "signed out", so a component
    // does not flash its signed-out branch and then swap.
    return { isLoaded: false, isSignedIn: undefined, user: undefined } as unknown as ClerkUserHook;
  }
  if (!devMe) return clerk;

  return { isLoaded: true, isSignedIn: true, user: devUser(devMe) } as unknown as ClerkUserHook;
}

export function useAuth(): ClerkAuthHook {
  const clerk = useClerkAuth();
  const devMe = useDevMe();

  if (!DEV || clerk.isSignedIn || !clerk.isLoaded) return clerk;
  if (devMe === undefined) {
    return { ...clerk, isLoaded: false, isSignedIn: undefined } as unknown as ClerkAuthHook;
  }
  if (!devMe) return clerk;

  return {
    ...clerk,
    isLoaded: true,
    isSignedIn: true,
    userId: devMe.userId,
    // No Clerk session exists, so there is no token to hand out. Nothing in the
    // reused surface calls getToken() (the API authenticates the dev bypass by
    // the same env var, on the cookie-less request) — but return null rather
    // than a fake JWT so a future caller fails loudly instead of sending junk.
    getToken: async () => null,
  } as unknown as ClerkAuthHook;
}

export function SignedIn({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth();
  return isSignedIn ? <>{children}</> : null;
}

export function SignedOut({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  return isLoaded && !isSignedIn ? <>{children}</> : null;
}
